import * as React from 'react';
import {toast} from 'react-toastify';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {
  useDropzone,
  DropzoneOptions,
  FileError,
} from 'react-dropzone';
import {
  FileType,
  imageTypes,
  videoTypes,
  audioTypes,
  documentTypes,
  UploadFileType,
  uploadFile, FileObject, uploadLink,
} from 'api/library';
import InputSubmit, {InputSubmitValues} from 'components/InputSubmit';
import {InputVariant} from 'components/Input';
import useModal from 'hooks/use-modal';
import {transition} from 'helpers/css';
import {
  IconDirectDownload,
  IconUploadDocument,
  IconUploadImage,
  IconUploadVideo,
} from 'icons';
import isError from 'helpers/isError';
import FilePicker, {FilePickerProps} from './FilePicker';
import Button, {ButtonVariant} from './Button';
import {TabbarProps} from './Tabbar';
import {Theme} from '../stores/AppStore';

export enum UploadVariant {
  Default = 'default',
  Accent = 'accent',
}

export interface UploadProps {
  type?: FileType;
  variant?: UploadVariant;
  icon?: React.ReactNode;
  title?: string;
  tagline?: string;
  tabbarProps?: Omit<TabbarProps, 'children'>;
  picker?: Pick<FilePickerProps, 'multiple' | 'onSelect'>;
  renderAfterInput?: React.ReactNode | React.ReactNodeArray;
  onUpload?(file: FileObject): void;
}

export const Container = styled.div`
  row-gap: 20px;
  display: grid;
`;

export const UploadContainer = styled.div<Pick<DropzoneOptions, 'disabled'>>`
  ${transition()};
  cursor: ${({disabled}) => !disabled && 'pointer'};
  border-radius: 5px;
  text-align: center;
  row-gap: 8px;
  display: grid;
`;

export const IconContainer = styled.div`
  display: inline-block;
  color: ${({theme}) => theme.name === Theme.Dark ? (
    transparentize(.8, theme.colors.white)
  ) : (
    transparentize(.8, theme.variables.color)
  )};
`;

export const Title = styled.span`
  font-size: 22px;
  font-weight: 600;
  line-height: 28px;
  user-select: none;
`;

export const Tagline = styled.span`
  user-select: none;
  font-size: 13px;
  font-weight: 500;
  color: ${({theme}) => `
    ${transparentize(.7, theme.variables.color)};
  `};
`;

export const Note = styled.span`
  margin: 12px auto 0 auto;
  max-width: 264px;
  font-size: 13px;
  line-height: 18px;
`;

export const InputContainer = styled.div`
  grid-template-columns: 1fr max-content;
  display: grid;
`;

export const AfterInput = styled.div`
  margin-left: 6px;
`;

/**
 * The upload component
 * @param {UploadProps} props
 * @return {React.ReactElement<UploadProps>}
 * @constructor
 */
function Upload(
    props: UploadProps,
): React.ReactElement<UploadProps> {
  const ModalPicker = useModal();

  /**
   * File validator
   * @param {File} file
   * @return {FileError | null}
   */
  function validator(file: File): FileError | null {
    /**
     * Is file type valid
     * @return {boolean}
     */
    function isValid(): boolean {
      switch (props.type) {
        case FileType.Image: return imageTypes.find((type) => (
          file.type === type
        )) !== undefined;
        case FileType.Document: return documentTypes.find((type) => (
          file.type === type
        )) !== undefined;
        case FileType.Video: return videoTypes.find((type) => (
          file.type === type
        )) !== undefined;
        case FileType.Audio: return audioTypes.find((type) => (
          file.type === type
        )) !== undefined;
        default: return [
          ...imageTypes,
          ...documentTypes,
          ...videoTypes,
          ...audioTypes,
        ].find((type) => (
          file.type === type
        )) !== undefined;
      }
    }

    if (isValid()) {
      return null;
    }

    const errorMessage: string = 'Не правильный тип файла';
    toast.error(errorMessage, {
      pauseOnFocusLoss: false,
      draggable: false,
    });

    return {
      code: 'error-type',
      message: errorMessage,
    };
  }

  const [
    disabled,
    setDisabled,
  ] = React.useState<boolean>(false);

  const {
    getRootProps,
    getInputProps,
  } = useDropzone({
    maxFiles: 1,
    multiple: false,
    disabled,
    validator,
    onDropAccepted: handleDropAccepted,
  });

  /**
   * Upload the file
   * @param {UploadFileType} file
   */
  function upload(file: UploadFileType): void {
    setDisabled(true);
    const statusToast = toast.info('Загрузка...', {
      closeButton: false,
      closeOnClick: false,
      autoClose: false,
      draggable: false,
    });
    uploadFile(file).then((response) => {
      if (response && !isError(response)) {
        props.onUpload && props.onUpload(response);
        toast.update(statusToast, {
          type: 'success',
          render: 'Файл загружен.',
          pauseOnFocusLoss: false,
          autoClose: 2000,
        });
      } else {
        toast.update(statusToast, {
          type: 'error',
          render: 'Произошла ошибка. Пожалуйста перезагрузите страницу.',
          pauseOnFocusLoss: false,
          autoClose: 2000,
        });
      }
      setDisabled(false);
    });
  }

  /**
   * The handle drop accepted
   * @param {File[]} files
   */
  function handleDropAccepted(files: File[]): void {
    upload(files[0]);
  }

  /**
   * Get the icon
   * @return {JSX.Element}
   */
  function getIcon(): JSX.Element {
    switch (props.type) {
      case FileType.Image: return <IconUploadImage />;
      case FileType.Document: return <IconUploadDocument />;
      case FileType.Video: return <IconUploadVideo />;
      default: return <IconDirectDownload />;
    }
  }

  /**
   * Get the title
   * @return {string}
   */
  function getTitle(): string {
    switch (props.type) {
      case FileType.Image: return 'Загрузить изображение';
      case FileType.Document: return 'Загрузить документ';
      case FileType.Video: return 'Загрузить видео';
      default: return 'Загрузить файл';
    }
  }

  /**
   * Get the tagline
   * @return {string | undefined}
   */
  function getTagline(): string | undefined {
    switch (props.type) {
      case FileType.Image: return '.jpg .jpeg .png .gif (5 Mb)';
      case FileType.Document: return '.pdf .doc .docx .xls .ppt .pptx (5 Mb)';
      case FileType.Video: return '.mp4 .mp3 .wav (до 100 Mb)';
    }
  }

  /**
   * Render input
   * @return {JSX.Element}
   */
  function renderInput() {
    /**
     * Handle submit the input submit component
     * @param {InputSubmitValues} values
     */
    function handleSubmit(values: InputSubmitValues): void {
      const statusToast = toast.info('Загрузка...', {
        closeButton: false,
        closeOnClick: false,
        autoClose: false,
        draggable: false,
      });
      uploadLink(String(values.value)).then((response) => {
        if (response && !isError(response)) {
          props.onUpload && props.onUpload(response);
          toast.update(statusToast, {
            type: 'success',
            render: 'Файл загружен.',
            pauseOnFocusLoss: false,
            autoClose: 2000,
          });
        } else {
          toast.update(statusToast, {
            type: 'error',
            render: 'Произошла ошибка. Пожалуйста перезагрузите страницу.',
            pauseOnFocusLoss: false,
            autoClose: 2000,
          });
        }
      });
    }

    return (
      <InputSubmit
        placeholder="Ссылка на файл. Enter чтобы добавить."
        variant={props.variant === UploadVariant.Default ? (
          InputVariant.Accent
        ) : undefined}
        onSubmit={handleSubmit}
      />
    );
  }

  /**
   * Render the modal file picker.
   * @return {JSX.Element | undefined}
   */
  function renderModalPicker(): JSX.Element | undefined {
    /**
     * Render the button control.
     * @return {JSX.Element}
     */
    function renderButton(): JSX.Element {
      /**
       * The click event handler.
       */
      function handleClick(): void {
        ModalPicker.setModalVisible(true);
      }

      return (
        <Button
          type="button"
          variant={ButtonVariant.Primary}
          onClick={handleClick}
        >
          Из библиотеки
        </Button>
      );
    }

    /**
     * Render the modal.
     * @return {JSX.Element}
     */
    function renderModal(): JSX.Element | undefined {
      /**
       * The modal blur event handler.
       */
      function handleBlur(): void {
        ModalPicker.setModalVisible(
            !ModalPicker.isModalVisible,
        );
      }

      /**
       * The picker select event handler.
       * @param {FileObject[]} attached
       */
      function handleSelect(attached: FileObject[]): void {
        props.picker?.onSelect && props.picker?.onSelect(attached);
      }

      return ModalPicker.isModalVisible ? (
        <FilePicker
          type={props.type}
          multiple={props.picker?.multiple}
          tabbarProps={props.tabbarProps}
          onBlur={handleBlur}
          onSelect={handleSelect}
        />
      ) : undefined;
    }

    return props.picker ? (
      <React.Fragment>
        {renderButton()}
        {renderModal()}
      </React.Fragment>
    ) : undefined;
  }

  return (
    <Container>
      <UploadContainer {...getRootProps()}>
        <input {...getInputProps()} />
        <IconContainer>
          {props.icon || getIcon()}
        </IconContainer>
        <Title>
          {props.title || getTitle()}
        </Title>
        {props.tagline || getTagline() && (
          <Tagline>
            {getTagline()}
          </Tagline>
        )}
        <Note>
          Кликните или перетащите файл, укажите ссылку
          или добавьте из библиотеки
        </Note>
      </UploadContainer>
      <InputContainer id="upload-input-container">
        {renderInput()}
        <AfterInput>
          {renderModalPicker()}
        </AfterInput>
      </InputContainer>
    </Container>
  );
}

Upload.defaultProps = {
  variant: UploadVariant.Default,
};

export default Upload;
